import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Clock, Globe } from 'lucide-react';
import GrainOverlay from './GrainOverlay';

const Stats: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats = [
    { number: 1000, label: 'Healthcare Professionals', suffix: '', icon: Users },
    { number: 50, label: 'Medical Practices', suffix: '', icon: Building2 },
    { number: 99, label: '% Uptime', suffix: '%', icon: Clock },
    { number: 24, label: 'Countries', suffix: '', icon: Globe },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-secondary-50/30 dark:bg-dark-900/30 relative overflow-hidden">
      <GrainOverlay />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
            >
              {/* Circular icon container */}
              <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                <stat.icon className="w-7 h-7 text-primary-500" />
              </div>

              {/* Number */}
              <motion.div
                className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-dark-900 dark:text-dark-100 mb-2"
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                {isVisible && (
                  <AnimatedNumber
                    value={stat.number}
                    suffix={stat.suffix}
                    duration={2000}
                  />
                )}
              </motion.div>

              {/* Label */}
              <p className="text-base md:text-lg text-dark-500 dark:text-dark-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AnimatedNumber: React.FC<{ value: number; suffix: string; duration: number }> = ({
  value,
  suffix,
  duration,
}) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const increment = value / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCurrent(value);
        clearInterval(timer);
      } else {
        setCurrent(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{Math.floor(current)}{suffix}</span>;
};

export default Stats;
